import {z} from 'zod';

import {Locale} from '@dms/api/base';
import {EntityResourceIds, PhoneNumber, UuidResponseBody} from '@dms/api/shared';

export type SecondaryRecordId = {
  resourceId: EntityResourceIds;
  recordId: string;
};

export type PatchSentToastNotificationApiResponse = SentToastNotificationResponseBody;
export type PatchSentToastNotificationApiArg = {
  id: string;
  body: PatchSentToastNotificationRequestBody;
};

export type GetSentToastNotificationApiResponse = SentToastNotificationResponseBody;
export type GetSentToastNotificationApiArg = {
  id: string;
};

export type ListSentToastNotificationsApiResponse = ListSentToastNotificationsResponseBody;
export type ListSentToastNotificationsApiArg = {
  limit?: number;
  after?: string;
};

export type ListSentToastNotificationsResponseBody = {
  notifications: SentToastNotificationResponseBody[];
  count: number;
  hasMoreNotifications: boolean;
};

export type MessagingAllowlistChannel = 'email' | 'sms';

export type MessagingAllowlistResponseBody = {
  enabled: boolean;
  patterns: string[];
};

export type UpdateMessagingAllowlistRequestBody = {
  enabled: boolean;
  patterns: string[];
};

export type GetMessagingAllowlistApiArg = {
  channel: MessagingAllowlistChannel;
};
export type GetMessagingAllowlistApiResponse = MessagingAllowlistResponseBody;

export type UpdateMessagingAllowlistApiArg = {
  channel: MessagingAllowlistChannel;
  body: UpdateMessagingAllowlistRequestBody;
};
export type UpdateMessagingAllowlistApiResponse = /** status 204  */ void;

export type SendCustomerEmailApiResponse = /** status 204  */ void;
export type SendCustomerEmailApiArg = {
  /** User JWT tokens are allowed */
  authorization?: string;
  sendCustomerEmailRequestBody: SendCustomerEmailRequestBody;
};

export type BusinessCaseOnlineOfferPayloadRequestBody = {
  businessCaseId: string;
  vehicleId: string;
  photoId: string | null;
  sellerId: string;
  onlineOfferUri: string;
  body: string;
};
export type AttachmentRequestBody = {
  name: string;
  url: string;
};
export type ServiceCheckInPayloadRequestBody = {
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBody[];
};
export type ServiceHandoverPayloadRequestBody = {
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBody[];
};
export type GeneralCustomerMessagePayloadRequestBody = {
  subject: string;
  body: string;
};
export type CustomerMessageRequestBody = {
  customerId: string;
  customerMessageId:
    | 'business_case-online_offer'
    | 'customer-message'
    | 'service-customer_check_in'
    | 'service-customer_handover';
  payload:
    | BusinessCaseOnlineOfferPayloadRequestBody
    | ServiceCheckInPayloadRequestBody
    | ServiceHandoverPayloadRequestBody
    | GeneralCustomerMessagePayloadRequestBody;
  locale?: string | null;
};
export type SendCustomerEmailAttachmentRequestBody = {
  fileId: string;
  convertToPdf?: boolean;
};
export type SendCustomerEmailRequestBody = {
  subject: string;
  body: string;
  customerId: string;
  customerEmailAddress: string;
  attachments?: SendCustomerEmailAttachmentRequestBody[];
  recordId: string | null;
  smtpCredentialsId: string;
  copyTo?: string[];
  resourceId: EntityResourceIds | null;
  secondaryRecordIds?: SecondaryRecordId[];
};

export type PatchSentToastNotificationRequestBody = {
  isRead?: boolean | null;
};

export type PatchUnreadSentToastNotificationsApiArg = {
  body: PatchSentToastNotificationRequestBody;
};

export type SentToastNotificationResponseBody =
  | ({
      type: 'batch_download-bulk_event_state_changed';
    } & BatchDownloadBulkEventStateChanged)
  | ({
      type: 'participation-assignee_assigned';
    } & ParticipationAssigneeAssigned)
  | ({
      type: 'participation-assignee_unassigned';
    } & ParticipationAssigneeUnassigned)
  | ({
      type: 'participation-owner_changed';
    } & ParticipationOwnerChanged)
  | ({
      type: 'vehicle_photo-rotate_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle_photo-remove_background_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle-file_operation_finished';
    } & VehicleFileOperationFinished)
  | ({
      type: 'vehicle-inspection_expiration_notice';
    } & VehicleInspectionExpirationNotice)
  | ({
      type: 'vehicle-next_service_notice';
    } & VehicleNextServiceNotice)
  | ({
      type: 'comment-mention';
    } & CommentMention)
  | ({
      type: 'comment-record_owner';
    } & CommentRecordOwner)
  | ({
      type: 'sale_vehicle-reservation_cancelled';
    } & SaleVehicleReservationCancelled)
  | ({
      type: 'access_control-user_permission_changed';
    } & PermissionChanged)
  | ({
      type: 'datagrid-custom_preset_shared';
    } & PresetChanged)
  | ({
      type: 'datagrid-data_export';
    } & DataGridExport)
  | ({
      type: 'sale_vehicle_photo-remove_background_finished';
    } & SaleVehiclePhotoRemoveBackgroundFinished)
  | ({
      type: 'file_storage-file_uploaded';
    } & FileStorageFileUploadedNotification)
  | ({
      type: 'file_storage-file_variant_uploaded';
    } & FileStorageFileVariantUploadedNotification)
  | ({
      type: 'vehicle-days_since_last_reprice_notice';
    } & VehicleDaysSinceLastRepriceNotification)
  | ({
      type: 'digital_signature-document_signed';
    } & DigitalDocumentSignedNotification)
  | ({
      type: 'vehicle-warranty_expiration_notice';
    } & VehicleWarrantyExpirationNotification)
  | ({
      type: 'vehicle-unregistered_notice';
    } & ToastNotificationVehicleUnregisteredNotice)
  | ({
      type: 'task_management-task_created';
    } & TaskCreatedNotification)
  | ({
      type: 'task_management-task_updated';
    } & TaskUpdatedNotification)
  | ({
      type: 'task_management-due_date_notice';
    } & TaskDueNoticeNotification)
  | ({
      type: 'task_management-task_commented';
    } & TaskCommentedNotification)
  | ({
      type: 'task_management-task_reporter_changed';
    } & TaskReporterChangedNotification)
  | ({
      type: 'custom_fields-expiration_notice';
    } & CustomFieldExpirationNotification)
  | ({
      type: 'advertisement-publishing_failed';
    } & ToastNotificationAdvertisementPublishingFailedNotice)
  | ({
      type: 'sourcing-filter_preset_update';
    } & ToastNotificationFilterPresetUpdateNotice)
  | ({
      type: 'vehicle-inspection_cloned';
    } & VehicleInspectionClonedNotification)
  | ({
      type: 'business_case-unread_message';
    } & ToastNotificationBusinessCaseHasUnreadMessage)
  | ({
      type: 'business_case-quick_unread_message';
    } & QuickToastNotificationBusinessCaseHasUnreadMessage)
  | ({
      type: 'import-finished';
    } & ImportFinishedNotification)
  | ({
      type: 'vehicle-inspection_to_review';
    } & VehicleInspectionToReviewNotification)
  | ({
      type: 'vehicle-inspection_returned_for_completion';
    } & VehicleInspectionReturnedToCompletionNotification)
  | ({
      type: 'vehicle-inspection_finished';
    } & VehicleInspectionFinishedNotification)
  | ({
      type: 'integrated_service-change_announced';
    } & IntegratedServiceChangeNotification)
  | ({
      type: 'integrated_service-external_planner_conflict';
    } & IntegratedServiceExternalPlannerConflictNotification)
  | ({
      type: 'prompt_bridge-status_changed';
    } & PromptBridgeStatusChangedNotification)
  | ({
      type: 'offer-validity_expiration';
    } & ToastNotificationOfferValidityExpiration)
  | ({
      type: 'magic_publishing-finished';
    } & MagicPublishingFinishedNotification);

export type ToastNotification = {
  id: string;
  type: string;
  createdAt: string;
  isRead: boolean;
};

export type ImportFinishedNotification = ToastNotification & {
  payload: {
    importId: string;
    fileType: 'carisSaleVehicleCosts' | 'as3SaleVehicleCosts';
    importStatus: 'created' | 'scheduled' | 'inProgress' | 'finished' | 'failed';
    author: User;
    startedAt: string;
    finishedAt: string;
    affectedRecordsCount: number;
    errorsCount: number;
  };
};

export type Record = {
  id: string;
  resourceId:
    | 'CUSTOMER'
    | 'BUSINESS_CASE'
    | 'INTEREST'
    | 'SERVICE_CASE'
    | 'VEHICLE'
    | 'SERVICE_ORDER'
    | 'TASK'
    | 'SOURCING_VEHICLE_OFFER';
  name: string;
};

export type User = {
  id: string;
  name: string;
};

export type ParticipationAssigneeAssigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    assignedAt: string;
  };
};
export type ParticipationAssigneeUnassigned = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    unassignedAt: string;
  };
};
export type PermissionChanged = ToastNotification;
export type DataGridExport = ToastNotification & {
  payload: {
    createdAt: string;
    exportId: string;
    dataGridCode: string;
    exportedAt: string;
    fileType: string;
  };
};
export type PresetChanged = ToastNotification & {
  payload: {
    createdAt: string;
    datagridCode: string;
    datagridPresetTitle: string;
    datagridTranslatedCode: string;
    sharedByUser: {id: string; name: string};
    sharedWithUser: {id: string; name: string};
  };
};

export type SaleVehiclePhotoRemoveBackgroundFinished = ToastNotification & {
  payload: {
    saleVehicle: {
      id: string;
      vehicleId: string;
      vehicleName: string;
    };
  };
};

export type ParticipationOwnerChanged = ToastNotification & {
  payload: {
    record: Record;
    actor: User;
    previousOwner: User | null;
    newOwner: User | null;
    changedAt: string;
  };
};
export type Vehicle = {
  id: string;
  name: string;
};
export type VehicleInspectionExpirationNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string;
    registrationPlate?: string;
    daysUntilExpiration: number;
    validUntil: string;
  };
};
export type VehicleNextServiceNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    vin?: string;
    registrationPlate?: string;
    daysUntilNextService: number;
    validUntil: string;
  };
};
export type ToastNotificationBusinessCaseHasUnreadMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record;
};
export type ToastNotificationBusinessCaseHasUnreadMessage = ToastNotification & {
  payload: ToastNotificationBusinessCaseHasUnreadMessagePayload;
};
export type QuickToastNotificationBusinessCaseHasUnreadMessagePayload = {
  customerId: string;
  customerName: string;
  record: Record;
};
export type QuickToastNotificationBusinessCaseHasUnreadMessage = ToastNotification & {
  payload: QuickToastNotificationBusinessCaseHasUnreadMessagePayload;
};
export type BatchDownloadBulkEventStateChanged = ToastNotification & {
  payload: {
    eventId: string;
    state: 'FAILED' | 'DONE';
    bulkObjectId: string | null;
    bulkObjectDownloadUrl: string | null;
  };
};
export type VehicleFileOperationFinished = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    operation?: 'REMOVE_BACKGROUND' | 'ROTATE';
  };
};
export type CommentMention = ToastNotification & {
  payload: {
    record: Record;
    author: User;
    commentId: string;
    commentHtml: string;
  };
};
export type CommentRecordOwner = CommentMention;

export type SaleVehicleReservationCancelled = ToastNotification & {
  payload: {
    vehicle: Vehicle;
  };
};

export type FileStorageFileUploadedNotification = ToastNotification & {
  payload: {
    fileId: string;
    fileStatus: string;
    fileType: string;
  };
};

export type FileStorageFileVariantUploadedNotification = ToastNotification & {
  payload: {
    originalFileId: string;
    variantFileId: string;
    fileStatus: string;
    fileType: string;
  };
};

export type VehicleDaysSinceLastRepriceNotification = ToastNotification & {
  payload: {
    daysSinceLastReprice: number;
    vehicle: Vehicle;
    vin: string | null;
  };
};

export type DigitalDocumentSignedNotification = ToastNotification & {
  payload: {
    record: Record | null;
    createdAt: string;
    signedBy: {
      id: string;
      name: string;
    };
    documentName: string;
    transactionType: string;
  };
};

export type VehicleWarrantyExpirationNotification = ToastNotification & {
  payload: {
    daysUntilExpiration: number;
    vehicle: Vehicle;
    vin: string | null;
    expiresAt: string;
  };
};

type TaskPayload = {
  task: {
    id: string;
    title: string;
  };
  user: {
    id: string;
    name: string;
  };
};

export type ToastNotificationVehicleUnregisteredNoticePayload = {
  vehicle: Vehicle;
  vin: string | null;
  daysUntilExpiration: number;
  registrationDueDate: string;
};

export type ToastNotificationVehicleUnregisteredNotice = ToastNotification & {
  payload: ToastNotificationVehicleUnregisteredNoticePayload;
};

export type TaskCreatedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type TaskUpdatedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type TaskDueNoticeNotification = ToastNotification & {
  payload: Pick<TaskPayload, 'task'> & {
    daysUntilDueDate: number;
    dueDate: string;
    dueTo: string;
  };
};

export type TaskCommentedNotification = ToastNotification & {
  payload: TaskPayload & {
    commentHtml: string;
  };
};

export type TaskReporterChangedNotification = ToastNotification & {
  payload: TaskPayload;
};

export type CustomFieldExpirationNotification = ToastNotification & {
  payload: {
    record: {
      id: string;
      resourceId: string;
      name: string;
      parent: unknown;
    };
    customFieldName: string;
    daysUntilExpiration: number;
    expiresAt: string;
  };
};

export type ToastNotificationAdvertisementPublishingFailedNotice = ToastNotification & {
  payload: {
    vehicle: Vehicle;
  };
};

export type ToastNotificationFilterPresetUpdateNotice = ToastNotification & {
  payload: {
    filterPresetId: string;
    filterPresetName: string;
    lastNotificationDate: string | null;
    currentNotificationDate: string;
    newVehiclesCount: number;
  };
};

export type Inspection = {
  id: string;
  name: string | null;
};

export type VehicleInspectionClonedNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
  };
};

export type VehicleInspectionToReviewNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type VehicleInspectionReturnedToCompletionNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type VehicleInspectionFinishedNotification = ToastNotification & {
  payload: {
    vehicle: Vehicle;
    inspection: Inspection;
    vin: string | null;
  };
};

export type IntegratedServiceChangeNotification = ToastNotification & {
  payload: {
    serviceCaseId: string;
    integratedServiceCode: 'MERCEDES_XENTRY_JOB' | 'DAIMLER_XENTRY_JOB';
    contextType: string;
    contextId: string;
  };
};

export type IntegratedServiceExternalPlannerConflictNotification = ToastNotification & {
  payload: {
    externalEventId: string;
    eventDateTime: string;
    contactInformation: string;
    recipients: string[];
  };
};

export type PromptBridgeStatusChangedNotification = ToastNotification & {
  payload: {
    messageId: string;
    status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED';
  };
};

export type ToastNotificationOfferValidityExpirationPayload = {
  businessCase: {
    id: string;
    number: string;
  };
  customer: {
    id: string;
    fullName: string;
  };
};

export type ToastNotificationOfferValidityExpiration = ToastNotification & {
  payload: ToastNotificationOfferValidityExpirationPayload;
};

export type MagicPublishingFinishedNotification = ToastNotification & {
  payload: {
    saleVehicle: {
      id: string;
      vehicleId: string;
      vehicleName: string;
    };
    urls: string[];
  };
};

export type ParameterResponseBody = {
  code: string;
  label: string;
  description: string;
  example: string;
};

export type ListContextParametersApiResponse = ParameterResponseBody[];
export type TemplateContextEnum =
  | 'customer'
  | 'service-order'
  | 'business-case'
  | 'rental-order'
  | 'vehicle';
export type ListContextParametersApiArg = {
  context: TemplateContextEnum;
};

export type CreateMessageTemplateRequestBody = {
  locale: string;
  name: string;
  context: TemplateContextEnum;
  body: string;
  subject?: string | null;
};
export type MessageTemplateResponseBody = {
  name: string;
  context: TemplateContextEnum;
  body: string;
  subject?: string | null;
};

export type CreateMessageTemplateApiResponse = UuidResponseBodyMessaging;
export type CreateMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  createTemplateRequestBody: CreateMessageTemplateRequestBody;
};

export type GetMessageTemplateApiResponse = MessageTemplateResponseBody;
export type GetMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
};

export type PatchMessageTemplateRequestBody = {
  name?: string | null;
  body?: string | null;
  subject?: string | null;
};

export type PatchMessageTemplateApiResponse = UuidResponseBodyMessaging;
export type PatchMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
  patchTemplateRequestBody: PatchMessageTemplateRequestBody;
};

export type DeleteMessageTemplateApiResponse = unknown;
export type DeleteMessageTemplateApiArg = {
  templateType: TemplateTypeEnum;
  templateId: string;
};
export type UuidResponseBodyMessaging = {
  id: string;
};
export type ContextTemplateResponseBody = {
  id: string;
  name: string;
  context: TemplateContextEnum;
  type: TemplateTypeEnum;
  body: string;
  subject?: string | null;
  locale?: Locale | null;
};

export type GetContextTemplateListApiResponse = ContextTemplateResponseBody[];
export type GetContextTemplateListApiArg = {
  type?: TemplateTypeEnum;
  context?: TemplateContextEnum;
};

export type TemplateTypeEnum = 'email' | 'sms' | 'chat';

export type EmailTemplateResponseBody = {
  name: string;
  type: TemplateTypeEnum;
  subject: string;
  body: string;
};
export type CustomerContextTemplateRequestBody = {
  customerId: string;
};
export type SmsTemplateResponseBody = {
  name: string;
  type: TemplateTypeEnum;
  body: string;
};

export type RenderEmailTemplateResponseBody = {
  subject: string;
  body: string;
};
export type RenderEmailTemplateRequestBody = {
  subject: string;
  body: string;
};
export type RenderCustomerEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  customerId: string;
};
export type RenderSmsTemplateResponseBody = {
  body: string;
};
export type RenderCustomerSmsTemplateRequestBody = {
  template: string;
  customerId: string;
};
export type RenderSmsTemplateRequestBody = {
  template: string;
};

export type RenderCustomerEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderCustomerEmailTemplateApiArg = {
  renderCustomerEmailTemplateRequestBody: RenderCustomerEmailTemplateRequestBody;
};
export type RenderCustomerEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export type RenderCustomerEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderCustomerSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderCustomerSmsTemplateApiArg = {
  renderCustomerSmsTemplateRequestBody: RenderCustomerSmsTemplateRequestBody;
};
export type RenderCustomerSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;
export type RenderCustomerSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};

export type RenderBusinessCaseEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  businessCaseId: string;
};

export type RenderBusinessCaseEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderBusinessCaseEmailTemplateApiArg = {
  renderBusinessCaseEmailTemplateRequestBody: RenderBusinessCaseEmailTemplateRequestBody;
};

export type RenderBusinessCaseSmsTemplateRequestBody = {
  template: string;
  businessCaseId: string;
};

export type RenderBusinessCaseSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderBusinessCaseSmsTemplateApiArg = {
  renderBusinessCaseSmsTemplateRequestBody: RenderBusinessCaseSmsTemplateRequestBody;
};

export type RenderServiceOrderEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  serviceOrderId: string;
  serviceCaseId: string;
};
export type RenderServiceOrderSmsTemplateRequestBody = {
  template: string;
  serviceOrderId: string;
  serviceCaseId: string;
};
export type RenderServiceOrderEmailTemplateApiResponse = RenderEmailTemplateResponseBody;
export type RenderServiceOrderEmailTemplateApiArg = {
  renderServiceOrderEmailTemplateRequestBody: RenderServiceOrderEmailTemplateRequestBody;
};
export type RenderServiceOrderEmailTemplateExampleApiResponse = RenderEmailTemplateResponseBody;
export type RenderServiceOrderEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderServiceOrderSmsTemplateApiResponse = RenderSmsTemplateResponseBody;
export type RenderServiceOrderSmsTemplateApiArg = {
  renderServiceOrderSmsTemplateRequestBody: RenderServiceOrderSmsTemplateRequestBody;
};
export type RenderServiceOrderSmsTemplateExampleApiResponse = RenderSmsTemplateResponseBody;
export type RenderServiceOrderSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};
export type Obligation = 'system' | 'admin' | 'user';
export type Channel =
  | 'email'
  | 'sms'
  | 'toastNotification'
  | 'desktopNotification'
  | 'appNotification';
export type ReceiveMessagePreference =
  | 'always'
  | 'ownerOrAssignee'
  | 'owner'
  | 'assignee'
  | 'never';
export type MessagingPreferences = {
  email: ReceiveMessagePreference;
  sms: ReceiveMessagePreference;
  toastNotification: ReceiveMessagePreference;
  desktopNotification: ReceiveMessagePreference;
  appNotification: ReceiveMessagePreference;
};
export type NotificationDefinitionResponseBody = {
  id: string;
  name: string;
  description: string;
  obligation: Obligation;
  channels: Channel[];
  defaultRecipientSettings: MessagingPreferences;
  isEnabled: boolean;
};
export type ListNotificationDefinitionsApiResponse = NotificationDefinitionResponseBody[];
export type ListNotificationDefinitionsApiArg = void;

export type GetNotificationDefinitionsApiArg = {id: string};
export type GetNotificationDefinitionsApiResponse = NotificationDefinitionResponseBody;

export type GetToastNotificationExampleApiResponse = SentToastNotificationResponseBody;
export type GetToastNotificationExampleApiArg = {
  id: string;
};

export type PreviewEmailNotificationApiResponse = string;
export type PreviewEmailNotificationApiArg = {
  id: string;
  locale: string;
};

export type Receive = 'always' | 'never';

export type NotificationReceiveSettingsResponseBody = {
  email: Receive | null;
  sms: Receive | null;
  toastNotification: Receive | null;
  desktopNotification: Receive | null;
  appNotification: Receive | null;
};
export type NotificationSettingsResponseBody = {
  systemReceiveSettings: NotificationReceiveSettingsResponseBody;
  /** Tenant may specify his own settings if the notification allows it, overriding system settings and applying as default to user settings. These settings might be overriden by users in case they are allowed to have their own settings and in case tenant settings are not forced. */
  tenantReceiveSettings: NotificationReceiveSettingsResponseBody | null;
  /** User may specify his own settings if the notification allows it, overriding system and tenant settings. These settings might be overriden by tenant forcing his settings. */
  userReceiveSettings: NotificationReceiveSettingsResponseBody | null;
  /** There will always be default system settings, if the notificaiton allows it. */
  systemSettings: any | null;
  /** There may be settings specified by tenant, if the notificaiton allows it. */
  tenantSettings: any | null;
  /** True if tenant settings are forced above user settings, false if not and null in case there is no tenant setting or there even can't be. */
  tenantSettingsForced: boolean | null;
};

export type PatchTenantNotificationSettingsApiResponse = /** status 204  */ void;
export type PatchTenantNotificationSettingsApiArg = {
  id: string;
  patchTenantNotificationSettingsRequestBody: PatchTenantNotificationSettingsRequestBody;
};
export type PatchUserNotificationSettingsApiResponse = /** status 204  */ void;
export type PatchUserNotificationSettingsApiArg = {
  id: string;
  patchUserNotificationSettingsRequestBody: PatchUserNotificationSettingsRequestBody;
};
export type PatchTenantNotificationSettingsRequestBody = {
  email?: Receive | null;
  sms?: Receive | null;
  toastNotification?: Receive | null;
  desktopNotification?: Receive | null;
  appNotification?: Receive | null;
  settings?: object | null;
  force?: boolean | null;
};
export type PatchUserNotificationSettingsRequestBody = {
  email?: Receive | null;
  sms?: Receive | null;
  toastNotification?: Receive | null;
  desktopNotification?: Receive | null;
  appNotification?: Receive | null;
};
export type GetNotificationSettingsApiResponse =
  /** status 200  */ NotificationSettingsResponseBody;
export type GetNotificationSettingsApiArg = {
  id: string;
};

export type CreateSignatureApiResponse = /** status 201  */ UuidResponseBody;
export type CreateSignatureApiArg = {
  /** User JWT tokens are allowed */
  authorization?: string;
  updateSignatureRequestBody: UpdateSignatureRequestBody;
};
export type ListSignaturesApiResponse = /** status 200  */ SignatureResponseBody[];
export type ListSignaturesApiArg = void;
export type RemoveSignatureApiResponse = /** status 204  */ void;
export type RemoveSignatureApiArg = {
  id: string;
};
export type UpdateSignatureApiResponse = /** status 204  */ void;
export type UpdateSignatureApiArg = {
  id: string;
  updateSignatureRequestBody: UpdateSignatureRequestBody;
};
export type UpdateSignatureRequestBody = {
  signature: string;
};
export type SignatureResponseBody = {
  id: string;
  signature: string;
};

export type EmailCredentialsOwnerType = 'user' | 'tenant';
export type CreateSmtpCredentialsRequestBody = {
  ownerType: EmailCredentialsOwnerType;
  title: string;
  host: string;
  port: number;
  username: string;
  password: string;
  emailAddress: string;
};

export type PatchOAuthEmailCredentialsApiResponse = /** status 204  */ void;
export type PatchOAuthEmailCredentialsApiArg = {
  id: string;
  patchOAuthEmailCredentialsRequestBody: PatchOAuthEmailCredentialsRequestBody;
};
export type PatchOAuthEmailCredentialsRequestBody = {
  title?: string | null;
  emailAddress?: string | null;
};

export type OAuthProviders = 'microsoft';

export type SmtpCredentialsResponseBody = {
  /** SMTP credentials identifier */
  id: string;
  /** Email credentials kind */
  type: 'smtp';
  ownerType: EmailCredentialsOwnerType;
  title: string;
  host: string;
  port: number;
  username: string;
  emailAddress: string;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string | null;
  /** Creator user ID */
  createdBy: string;
  /** Last updater user ID */
  updatedBy: string | null;
};
export type OAuthEmailCredentialsResponseBody = {
  id: string;
  type: 'oauth';
  ownerType: EmailCredentialsOwnerType;
  provider: OAuthProviders;
  title: string;
  emailAddress: string;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string | null;
  /** Creator user ID */
  createdBy: string;
  /** Last updater user ID */
  updatedBy: string | null;
};
export type ListEmailCredentialsResponseBody = {
  credentials: (SmtpCredentialsResponseBody | OAuthEmailCredentialsResponseBody)[];
};
export type PatchSmtpCredentialsRequestBody = {
  title?: string | null;
  host?: string | null;
  port?: number | null;
  username?: string | null;
  password?: string | null;
  emailAddress?: string | null;
};
export type MicrosoftOAuth2Purpose = 'calendar' | 'email';
export type StartMicrosoftOAuth2RequestBody = {
  callbackUrl: string;
  purposes: MicrosoftOAuth2Purpose[];
};
export type MicrosoftOAuth2AuthUrlResponseBody = {
  authUrl: string;
};
export type CompleteMicrosoftOAuth2RequestBody = {
  code: string;
  state: string;
  callbackUrl: string;
};
export type MicrosoftOAuth2IdentityResponseBody = {
  displayName: string | null;
  email: string | null;
};
export type CompleteMicrosoftOAuth2ResponseBody = {
  microsoftTokenId: string;
  identity: MicrosoftOAuth2IdentityResponseBody;
  purposes: MicrosoftOAuth2Purpose[];
};
export type CreateMicrosoftOAuth2CredentialsRequestBody = {
  ownerType: EmailCredentialsOwnerType;
  oAuthTokenId: string;
  emailAddress: string;
  title: string;
};

export type DiscoverEmailProviderApiResponse = /** status 200  */ DiscoverEmailProviderResponseBody;
export type DiscoverEmailProviderApiArg = {
  discoverEmailProviderRequestBody: DiscoverEmailProviderRequestBody;
};
export type ListEmailProviderApiResponse = /** status 200  */ ListEmailProviderResponseBody;
export type ListEmailProviderApiArg = void;
export type CreateEmailSmtpCredentialsApiResponse = /** status 201  */ UuidResponseBody;
export type CreateEmailSmtpCredentialsApiArg = {
  createSmtpCredentialsRequestBody: CreateSmtpCredentialsRequestBody;
};
export type StartMicrosoftOAuth2ApiResponse = /** status 200  */ MicrosoftOAuth2AuthUrlResponseBody;
export type StartMicrosoftOAuth2ApiArg = {
  startMicrosoftOAuth2RequestBody: StartMicrosoftOAuth2RequestBody;
};
export type CompleteMicrosoftOAuth2ApiResponse =
  /** status 200  */ CompleteMicrosoftOAuth2ResponseBody;
export type CompleteMicrosoftOAuth2ApiArg = {
  completeMicrosoftOAuth2RequestBody: CompleteMicrosoftOAuth2RequestBody;
};
export type CreateMicrosoftOAuth2CredentialsApiResponse = /** status 201  */ UuidResponseBody;
export type CreateMicrosoftOAuth2CredentialsApiArg = {
  createMicrosoftOAuth2CredentialsRequestBody: CreateMicrosoftOAuth2CredentialsRequestBody;
};
export type DeleteEmailCredentialsApiResponse = /** status 204  */ void;
export type DeleteEmailCredentialsApiArg = {
  id: string;
};
export type ListEmailCredentialsApiResponse = /** status 200  */ ListEmailCredentialsResponseBody;
export type ListEmailCredentialsApiArg = {
  ownerType?: EmailCredentialsOwnerType;
};
export type PatchEmailSmtpCredentialsApiResponse = /** status 204  */ void;
export type PatchEmailSmtpCredentialsApiArg = {
  id: string;
  /** User JWT tokens are allowed */
  authorization?: string;
  patchSmtpCredentialsRequestBody: PatchSmtpCredentialsRequestBody;
};
export type SmtpCredentialsStubResponseBody = {
  host: string | null;
  port: number | null;
  username: string | null;
  password: string | null;
  emailAddress: string | null;
};
export type SmtpEmailProviderCode =
  | 'gmail-smtp'
  | 'outlook-smtp'
  | 'seznam-smtp'
  | 'custom-smtp'
  | 'microsoft-oauth';
export type SmtpEmailProviderResponseBody = {
  code: SmtpEmailProviderCode;
  type: 'smtp';
  credentials: SmtpCredentialsStubResponseBody;
};
export type MicrosoftOAuthEmailProviderResponseBody = {
  code: 'microsoft-oauth';
  type: 'oauth2';
  credentials: null;
};
export type EmailProviderResponseBody =
  | SmtpEmailProviderResponseBody
  | MicrosoftOAuthEmailProviderResponseBody;
export type DiscoverEmailProviderResponseBody = {
  providers: SmtpEmailProviderResponseBody[];
};
export type DiscoverEmailProviderRequestBody = {
  emailAddress: string;
  password?: string | null;
};
export type ListEmailProviderResponseBody = {
  providers: EmailProviderResponseBody[];
};
export type GetLastKnownEmailCredentialsApiResponse = {
  smtpCredentialsId: string;
};

export type CustomerMessageIdEnum =
  | 'customer-message'
  | 'service-customer_check_in'
  | 'service-customer_handover'
  | 'business_case-online_offer';
export type GetLastKnownEmailCredentialsApiArg = {
  customerMessageId: CustomerMessageIdEnum;
};
export type SendCustomerSmsApiResponse = /** status 204  */ void;
export type SendCustomerSmsApiArg = {
  sendCustomerSmsRequestBody: SendCustomerSmsRequestBody;
};
export type SendCustomerSmsRequestBody = {
  body: string;
  customerId: string;
  customerPhoneNumber: PhoneNumber;
  recordId?: string | null;
  resourceId?: string | null;
  secondaryRecordIds?: SecondaryRecordId[];
};

export type SendWhatsAppContentMessageApiResponse = /** status 204  */ void;
export type SendWhatsAppContentMessageApiArg = {
  sendWhatsAppContentMessageRequestBody: SendWhatsAppContentMessageRequestBody;
};
export type SendWhatsAppContentMessageRequestBody = {
  to: string;
  messageContent: string;
  customerId: string;
  recordId: string;
  resourceId: EntityResourceIds;
  chatConversationId?: string | null;
};

export type RenderCustomerMessageEmailTemplateApiResponse =
  /** status 200  */ RenderCustomerMessageEmailTemplateResponseBody;
export type RenderCustomerMessageEmailTemplateApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBody: RenderCustomerMessageRequestBody;
};
export type RenderCustomerMessageEmailTemplateResponseBody = {
  subject: string;
  body: string;
};

export type RenderCustomerMessageRequestBody = {
  customerId: string;
  customerMessageId?:
    | (
        | 'business_case-online_offer'
        | 'customer-message'
        | 'service-customer_check_in'
        | 'service-customer_handover'
      )
    | null;
  payload:
    | BusinessCaseOnlineOfferPayloadRequestBody
    | ServiceCheckInPayloadRequestBody
    | ServiceHandoverPayloadRequestBody
    | GeneralCustomerMessagePayloadRequestBody;
  locale?: string | null;
};

export type RenderCustomerMessageSmsTemplateApiResponse =
  /** status 200  */ RenderCustomerMessageTemplateResponseBody;
export type RenderCustomerMessageSmsTemplateApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBody: RenderCustomerMessageRequestBody;
};
export type RenderCustomerMessageTemplateResponseBody = {
  body: string;
};
export type RenderVehicleEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  vehicleId: string;
};
export type RenderVehicleSmsTemplateRequestBody = {
  template: string;
  vehicleId: string;
};
export type RenderRentalOrderSmsTemplateRequestBody = {
  template: string;
  rentalOrderId: string;
};
export type RenderRentalOrderEmailTemplateRequestBody = {
  template: RenderEmailTemplateRequestBody;
  rentalOrderId: string;
};
export type RenderRentalOrderEmailTemplateApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderRentalOrderEmailTemplateApiArg = {
  body: RenderRentalOrderEmailTemplateRequestBody;
};
export type RenderRentalOrderEmailTemplateExampleApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderRentalOrderEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderRentalOrderSmsTemplateApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderRentalOrderSmsTemplateApiArg = {
  body: RenderRentalOrderSmsTemplateRequestBody;
};
export type RenderRentalOrderSmsTemplateExampleApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderRentalOrderSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};
export type RenderVehicleEmailTemplateApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderVehicleEmailTemplateApiArg = {
  body: RenderVehicleEmailTemplateRequestBody;
};
export type RenderVehicleEmailTemplateExampleApiResponse =
  /** status 200  */ RenderEmailTemplateResponseBody;
export type RenderVehicleEmailTemplateExampleApiArg = {
  body: RenderEmailTemplateRequestBody;
};
export type RenderVehicleSmsTemplateApiResponse = /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderVehicleSmsTemplateApiArg = {
  body: RenderVehicleSmsTemplateRequestBody;
};
export type RenderVehicleSmsTemplateExampleApiResponse =
  /** status 200  */ RenderSmsTemplateResponseBody;
export type RenderVehicleSmsTemplateExampleApiArg = {
  body: RenderSmsTemplateRequestBody;
};

export type CustomerMessageDefinitionResponseBody = {
  id: string;
  name: string;
  description: string;
  channels: Channel[];
  deliveryType: 'manual' | 'automatic';
  hasSettings: boolean;
  context: TemplateContextEnum;
};
export type GetCustomerMessageDefinitionApiResponse =
  /** status 200  */ CustomerMessageDefinitionResponseBody;
export type GetCustomerMessageDefinitionApiArg = {
  id: string;
  /** Authorization profile id */
  'X-Authorization-Profile'?: string;
  /** User JWT tokens are allowed */
  authorization?: string;
};

export type SchedulerLatestRunResponseBody = {
  name: string;
  scheduledFor: string;
  lastRanAt: string | null;
};

export type ListSchedulerLatestRunsResponseBody = {
  runs: SchedulerLatestRunResponseBody[];
};

export type ListLatestSchedulerRunsApiResponse =
  /** status 200  */ ListSchedulerLatestRunsResponseBody;
export type ListLatestSchedulerRunsApiArg = void;

export type DeleteLatestSchedulerRunApiResponse = /** status 204  */ void;
export type DeleteLatestSchedulerRunApiArg = {
  schedulerName: string;
};

export type RunSchedulerApiResponse = /** status 204  */ void;
export type RunSchedulerApiArg = {
  schedulerName: string;
};
export type RenderCustomerMessageEmailTemplateV2ApiArg = {
  customerMessageId: string;
  renderCustomerMessageRequestBodyV2: RenderCustomerMessageRequestBodyV2;
};
export type RenderCustomerMessageEmailTemplateV2ResponseBody = {
  subject: string;
  body: string;
};

export type BusinessCaseOnlineOfferPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-online_offer';
  businessCaseId: string;
  vehicleId: string;
  photoId: string | null;
  sellerId: string;
  onlineOfferUri: string;
  body: string;
};
export type MessageRequestBodyV2 = {
  date: string;
  content: string;
};
export type BusinessCaseUnreadConversationPayloadRequestBodyV2 = {
  customerMessageId: 'business_case-unread_conversation';
  businessCaseId: string;
  vehicleId: string;
  sellerId: string;
  onlineOfferUri: string;
  messages: MessageRequestBodyV2[];
  moreMessagesCount: number;
};
export type GeneralCustomerMessagePayloadRequestBodyV2 = {
  customerMessageId: 'customer-message';
  subject: string;
  body: string;
};
export type AttachmentRequestBodyV2 = {
  name: string;
  url: string;
};
export type ServiceCheckInPayloadRequestBodyV2 = {
  customerMessageId: 'service-customer_check_in';
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBodyV2[];
};
export type ServiceHandoverPayloadRequestBodyV2 = {
  customerMessageId: 'service-customer_handover';
  serviceCaseId: string;
  digitalCertificateUrl: string;
  attachments: AttachmentRequestBodyV2[];
};
export type ServiceOrderContextPayloadRequestBodyV2 = {
  serviceCaseId: string;
  serviceOrderId: string;
};
export type ServiceOrderContextOptionalPayloadRequestBodyV2 = {
  customerMessageId: 'workshop-service_order_completed' | 'workshop-service_order_planned';
  context: ServiceOrderContextPayloadRequestBodyV2 | null;
};
export type VehicleContextPayloadRequestBodyV2 = {
  vehicleId: string;
};
export type VehicleContextOptionalPayloadRequestBodyV2 = {
  customerMessageId:
    | 'vehicle-inspection_expiration_reminder'
    | 'vehicle-regular_service_inspection_reminder';
  context: VehicleContextPayloadRequestBodyV2 | null;
};

export type EmailContextTemplate = {
  subject: string;
  body: string;
};

export type PreviewCustomerMessages =
  | 'vehicle-regular_service_inspection_reminder'
  | 'vehicle-inspection_expiration_reminder'
  | 'workshop-service_order_planned'
  | 'workshop-service_order_completed';
export type RenderCustomerMessageRequestBodyV2 = {
  payload:
    | ({
        customerMessageId: 'business_case-online_offer';
      } & BusinessCaseOnlineOfferPayloadRequestBodyV2)
    | ({
        customerMessageId: 'business_case-unread_conversation';
      } & BusinessCaseUnreadConversationPayloadRequestBodyV2)
    | ({
        customerMessageId: 'customer-message';
      } & GeneralCustomerMessagePayloadRequestBodyV2)
    | ({
        customerMessageId: 'service-customer_check_in';
      } & ServiceCheckInPayloadRequestBodyV2)
    | ({
        customerMessageId: 'service-customer_handover';
      } & ServiceHandoverPayloadRequestBodyV2)
    | ({
        customerMessageId: 'workshop-service_order_completed';
      } & ServiceOrderContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'workshop-service_order_planned';
      } & ServiceOrderContextOptionalPayloadRequestBodyV2)
    | ({
        customerMessageId: 'vehicle-inspection_expiration_reminder';
      } & VehicleContextOptionalPayloadRequestBodyV2)
    | (({
        customerMessageId: 'vehicle-regular_service_inspection_reminder';
      } & VehicleContextOptionalPayloadRequestBodyV2) & {
        emailContextTemplate: EmailContextTemplate | null;
      });
  locale: string;
  customerId?: string | null;
};

export const userMailboxProviderSchema = z.enum(['google', 'microsoft', 'imap']);
export type UserMailboxProvider = z.infer<typeof userMailboxProviderSchema>;

export const mailboxConnectionStatusSchema = z.enum([
  'CONNECTED',
  'CONNECTING',
  'ERROR',
  'AUTH_ERROR',
]);
export type MailboxConnectionStatus = z.infer<typeof mailboxConnectionStatusSchema>;

export const createUserOAuthMailboxRequestBodySchema = z.object({
  name: z.string(),
  redirectUri: z.string(),
});
export type CreateUserOAuthMailboxRequestBody = z.infer<
  typeof createUserOAuthMailboxRequestBodySchema
>;

export const createdOAuthMailboxResponseBodySchema = z.object({
  id: z.string(),
  oauthUrl: z.string(),
});
export type CreatedOAuthMailboxResponseBody = z.infer<typeof createdOAuthMailboxResponseBodySchema>;

export type CreateGoogleUserMailboxApiResponse = /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateGoogleUserMailboxApiArg = {
  createUserGoogleMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};
export type CreateMicrosoftUserMailboxApiResponse =
  /** status 201  */ CreatedOAuthMailboxResponseBody;
export type CreateMicrosoftUserMailboxApiArg = {
  createUserMicrosoftMailboxRequestBody: CreateUserOAuthMailboxRequestBody;
};

export const createUserImapMailboxRequestBodySchema = z.object({
  name: z.string(),
  host: z.string(),
  port: z.number(),
  username: z.string(),
  password: z.string(),
  secure: z.boolean(),
});
export type CreateUserImapMailboxRequestBody = z.infer<
  typeof createUserImapMailboxRequestBodySchema
>;
export type UpdateUserImapMailboxRequestBody = CreateUserImapMailboxRequestBody;

export type CreateImapUserMailboxApiResponse = /** status 201  */ UuidResponseBody;
export type CreateImapUserMailboxApiArg = {
  createUserImapMailboxRequestBody: CreateUserImapMailboxRequestBody;
};
export type UpdateImapUserMailboxApiResponse = /** status 204  */ void;
export type UpdateImapUserMailboxApiArg = {
  mailboxId: string;
  updateUserImapMailboxRequestBody: UpdateUserImapMailboxRequestBody;
};
export type DeleteUserMailboxApiResponse = /** status 204  */ void;
export type DeleteUserMailboxApiArg = {
  mailboxId: string;
};

export const userMailboxResponseBodySchema = z.object({
  id: z.string(),
  name: z.string(),
  accountId: z.string(),
  type: userMailboxProviderSchema,
  createdAt: z.string(),
});
export type UserMailboxResponseBody = z.infer<typeof userMailboxResponseBodySchema>;

export const listUserMailboxesResponseBodySchema = z.object({
  mailboxes: z.array(userMailboxResponseBodySchema),
});
export type ListUserMailboxesResponseBody = z.infer<typeof listUserMailboxesResponseBodySchema>;

export type ListUserMailboxesApiResponse = /** status 200  */ ListUserMailboxesResponseBody;
export type ListUserMailboxesApiArg = void;

export const reauthenticateUserMailboxRequestBodySchema = z.object({
  redirectUri: z.string(),
});
export type ReauthenticateUserMailboxRequestBody = z.infer<
  typeof reauthenticateUserMailboxRequestBodySchema
>;

export const reauthenticatedMailboxResponseBodySchema = z.object({
  oauthUrl: z.string(),
});
export type ReauthenticatedMailboxResponseBody = z.infer<
  typeof reauthenticatedMailboxResponseBodySchema
>;

export type ReauthenticateUserMailboxApiResponse =
  /** status 200  */ ReauthenticatedMailboxResponseBody;
export type ReauthenticateUserMailboxApiArg = {
  mailboxId: string;
  reauthenticateUserMailboxRequestBody: ReauthenticateUserMailboxRequestBody;
};

export const getUserMailboxConnectionStatusResponseBodySchema = z.object({
  status: mailboxConnectionStatusSchema,
});
export type GetUserMailboxConnectionStatusResponseBody = z.infer<
  typeof getUserMailboxConnectionStatusResponseBodySchema
>;

export type GetUserMailboxConnectionStatusApiResponse =
  /** status 200  */ GetUserMailboxConnectionStatusResponseBody;
export type GetUserMailboxConnectionStatusApiArg = {
  mailboxId: string;
};

export const customerMessageDirectionSchema = z.enum(['INCOMING', 'OUTGOING']);
export type CustomerMessageDirection = z.infer<typeof customerMessageDirectionSchema>;

export const customerMessageChannelSchema = z.enum(['email', 'sms', 'whatsapp']);
export type CustomerMessageChannel = z.infer<typeof customerMessageChannelSchema>;

export const customerMessageAttachmentResponseBodySchema = z.object({
  filename: z.string(),
  uri: z.string(),
  contentType: z.string().nullable(),
  size: z.number().nullable(),
});
export type CustomerMessageAttachmentResponseBody = z.infer<
  typeof customerMessageAttachmentResponseBodySchema
>;

export const customerMessageSecondaryRecordResponseBodySchema = z.object({
  recordId: z.string(),
  resourceId: z.string(),
});
export type CustomerMessageSecondaryRecordResponseBody = z.infer<
  typeof customerMessageSecondaryRecordResponseBodySchema
>;

export const customerMessageResponseBodySchema = z.object({
  id: z.string(),
  customerId: z.string().nullable(),
  senderId: z.string().nullable(),
  direction: customerMessageDirectionSchema,
  channel: customerMessageChannelSchema,
  contact: z.string(),
  content: z.string(),
  subject: z.string().nullable(),
  createdAt: z.string(),
  attachments: z.array(customerMessageAttachmentResponseBodySchema),
  recordId: z.string().nullable(),
  resourceId: z.string().nullable(),
  secondaryRecords: z.array(customerMessageSecondaryRecordResponseBodySchema),
});
export type CustomerMessageResponseBody = z.infer<typeof customerMessageResponseBodySchema>;

export const listCustomerMessagesResponseBodySchema = z.object({
  messages: z.array(customerMessageResponseBodySchema),
});
export type ListCustomerMessagesResponseBody = z.infer<
  typeof listCustomerMessagesResponseBodySchema
>;

export type ListCustomerMessagesApiResponse = /** status 200  */ ListCustomerMessagesResponseBody;
export type ListCustomerMessagesApiArg = {
  customerId: string;
  limit?: number;
  offset?: number;
};
